import * as Sentry from "@sentry/react";
import { formatDate, now } from "common/src/helpers/dateHelper";
import { Dayjs } from "dayjs";
import { first, isEmpty, isEqual, omit, sortBy } from "lodash";
import { InfiniteData, QueryKey } from "react-query";
import {
    Event,
    Experience,
    ExperiencePurchaseItem,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
    Response,
    Timeslot,
    TimeslotPlaceholder,
    TimeslotPlaceholderObject,
} from "types";
import { api } from "@/api/api";
import { queryClient } from "@/api/queryClient";
import { refetchMerchandiseCatalogItems } from "@/components/PurchaseCreate/helpers/PurchaseCreateHelpers";
import { getResourceByQueryKey, updateCollectionByQueryKey, updateResourceCache } from "@/helpers/cacheHelper";
import { Page } from "@/hooks/helper";
import { AccountState } from "@/hooks/useAccount";
import { updatePurchaseNotificationsCache } from "@/hooks/usePurchaseNotifications";
import { getStart, getTimeslotIdByEvent } from "@/models/Event";
import { hasUniqueGuestIdentification } from "@/models/Experience";
import { getTimeslotIdFromPurchaseItem } from "@/models/PurchaseItem";
import { invalidateSellerBalance } from "@/models/Seller";
import { isAffiliate } from "@/models/User";
import { isTableView, useDashboardFilterStore } from "@/stores/dashboardFilterStore";
import { defaultFilters, usePurchaseFilterStore } from "@/stores/purchaseFilterStore";
import { usePurchaseSearchStore } from "@/stores/purchaseSearchStore";
import { DashboardContext, DashboardMode, DashboardScope } from "@/types/Dashboard";
import { removeCacheItem, updateCache } from "@/utils/updateCache";

// This method updates the placeholder by the  new timeslot, In case when there are multiple timeslots in a placeholder,
// we move the placeholder to the new timeslot and remove from the old one.
export const updateTimeslotsCollectionWithPlaceholder = async (
    existingTimeslot: Timeslot,
    placeholder: TimeslotPlaceholder,
    position: "before" | "after",
) => {
    const placeholderObjects = placeholder[position];
    if (!placeholderObjects || !placeholder.queryKey) {
        return;
    }

    const placeholderObject = placeholderObjects.shift();
    if (!placeholderObject) {
        return;
    }

    const newTimeslot = await fetchTimeslotByEvent(placeholderObject.id)!;
    if (!newTimeslot) {
        return;
    }

    useDashboardFilterStore.getState().setIsRealTimeUpdate(true);
    updateCollectionByQueryKey<Timeslot>(placeholder.queryKey, (timeslots: Timeslot[]) => {
        return timeslots.flatMap((timeslot) => {
            if (timeslot.id === existingTimeslot.id) {
                const updatedTimeslot = omit(timeslot, `placeholder[${position}]`);
                const hasTimeslot = timeslots.some(({ id }) => id === newTimeslot.id);
                if (hasTimeslot) {
                    return updatedTimeslot;
                }

                if (placeholderObjects.length > 0) {
                    newTimeslot.placeholder = { queryKey: placeholder.queryKey, after: placeholderObjects };
                }

                return position === "before" ? [newTimeslot, updatedTimeslot] : [updatedTimeslot, newTimeslot];
            }

            return timeslot;
        });
    });
};

// We are finding the queryKey and its corresponding data for a new event in this method.
const getTimeslotsQueryData = (date: Dayjs): [QueryKey, InfiniteData<Page<Timeslot>>] | undefined => {
    const state = useDashboardFilterStore.getState();
    const mode = state.getMode();
    const range = getRangeByMode(mode);
    const { scope } = state.filters;

    const isListAllView = scope === DashboardScope.ALL && mode === DashboardMode.LIST;
    const isDayMode = mode === DashboardMode.DAY || mode === DashboardMode.TABLE;
    const hasNoEndDate = isListAllView || isDayMode;

    const queries = queryClient.getQueriesData<InfiniteData<Page<Timeslot>>>(["timeslots", "collection", "infinite"]);

    // Looping in reverse as in most cases the newest key in queryClient will be the one we need.
    for (let index = queries.length - 1; index >= 0; index--) {
        const [key, data] = queries[index];
        // Timeslots which we show in dashboard have queryKey like ["timeslots", "collection", "infinite", params]
        // We only want to find queries which matches this and not others.
        if (key.length === 4) {
            const params: any = key[3];
            const [startParam, endParam] = params.start.split(",");
            const start = now(startParam);
            const end = hasNoEndDate ? now(start).endOf("day") : now(endParam).endOf("day");
            const isSameContext = params.context === getDashboardContext();

            // endParam won't be there for DAY or LIST ALL view, only params.start will be used in these cases.
            const isSameRange =
                hasNoEndDate && !endParam ? start.isSame(now(params.start), "day") : range === end.diff(start, "days");

            const isEventInRange = date.isSameOrAfter(start.startOf("day")) && date.isSameOrBefore(end);

            // The timeslots queryKey for a new event will be the current key if the range for queryKey is same,
            // the dashboard context like LIST/MONTH etc is same and the new event exists between the start and end param of the key.
            // This ensures we update the correct queryKey. If it is not found then we don't update timeslots cache.
            // Whenever user opens the day in dashboard for which booking is made, we make timeslots API call.
            if (isSameRange && isSameContext && isEventInRange) {
                return [key, data];
            }
        }
    }
};

const getDashboardContext = () => {
    const { scope } = useDashboardFilterStore.getState().filters;

    switch (scope) {
        case DashboardScope.RESERVED:
            return DashboardContext.RESERVED;
        case DashboardScope.AVAILABLE:
            return DashboardContext.AVAILABLE;
        case DashboardScope.WAITLIST:
            return DashboardContext.WAITLIST;
        case DashboardScope.ALL:
            return DashboardContext.ALL;
        default:
            return null;
    }
};

export const getQueryKeyForRosterGuestList = (eventId: ID) => {
    return [
        "purchaseItems",
        "collection",
        "infinite",
        { event: eventId, expand: "waiver,membershipPurchaseItem,purchaseRuleLink,purchaseRule" },
    ];
};

export const getQueryKeyForDashboardTable = (arrivalDate: Dayjs) => {
    return [
        "purchaseItems",
        "collection",
        "infinite",
        {
            arrivalDate: formatDate(arrivalDate),
            expand: "waiver,membershipPurchaseItem,purchaseRuleLink,purchaseRule",
        },
    ];
};

export const fetchTimeslotByEvent = async (eventId: ID): Promise<Timeslot> => {
    const params: Record<string, string> = { event: eventId };
    const dashboardContext = getDashboardContext();
    if (dashboardContext) {
        params.context = dashboardContext;
    }

    const { data: timeslot } = await api.get<Response<Timeslot>>("timeslots", { params });

    return timeslot.data?.[0];
};

// Upserts the new timeslot in the cache, also handles removal of timeslot from cache (canceled purchase in reserved view makes reserved count 0)
const fetchTimeslotAndUpdateCache = async (
    queryKey: QueryKey,
    timeslotId: ID,
    eventId: string,
    itemInCache?: PurchaseItem,
) => {
    let existingTimeslot = getResourceByQueryKey(queryKey, timeslotId);
    let updateKey = timeslotId;

    // This is the case which will be in ALL or AVAILABLE views, If a event doesn't exist for a timeslots it will have id "experienceId_date",
    // If we get a new timeslot with ID "experienceId_date_eventId", we will update the existing timeslot with id "experienceId_date" instead of adding it to cache separately.
    if (!existingTimeslot) {
        const lastUnderscoreIndex = timeslotId.lastIndexOf("_");
        updateKey = timeslotId.slice(0, lastUnderscoreIndex);
        existingTimeslot = getResourceByQueryKey(queryKey, updateKey);
    }

    const updatedTimeslot = await fetchTimeslotByEvent(eventId);

    if (updatedTimeslot) {
        const shouldInsert = !timeslotId || !existingTimeslot;
        updateCache("timeslots", updatedTimeslot, false, updateKey, shouldInsert, queryKey);
    } else {
        removeCacheItem("timeslots", timeslotId, queryKey);
    }

    if (itemInCache && "arrivalDateTime" in itemInCache) {
        handleChangeArrival(queryKey, itemInCache, timeslotId);
    }
};

const addUniqueTimeslotToPlaceholder = (
    placeholder: TimeslotPlaceholderObject,
    placeholders: TimeslotPlaceholderObject[] = [],
) => {
    const index = placeholders.findIndex(({ id }) => id === placeholder.id);

    if (index === -1) {
        placeholders.push(placeholder);
    }

    return sortBy(placeholders, getSortKey);
};

const getSortKey = (placeholder: TimeslotPlaceholderObject) => {
    return [placeholder.start, placeholder.name.trimStart().toLowerCase()];
};

// If the timeslot already exists in cache, then find and update it
const updateTimeslotInCache = async (queryKey: QueryKey, timeslotId: ID, eventId: ID) => {
    const timeslotsData = queryClient.getQueryData<InfiniteData<Page<Timeslot>>>(queryKey);
    if (!timeslotsData?.pages) {
        return false;
    }

    let exists = false;
    if ("pages" in timeslotsData) {
        // Check all the pages in a query key if a timeslot exists
        for (const page of timeslotsData.pages) {
            if (page.data.some(({ id }) => id === timeslotId)) {
                exists = true;
                break;
            }
        }
    }

    if (!exists) {
        return false;
    }

    const newTimeslot = await fetchTimeslotByEvent(eventId);
    if (!newTimeslot) {
        return false;
    }

    updateCache("timeslots", newTimeslot, false, newTimeslot.id, false, queryKey);
    return true;
};

// This function updates timeslots cache when a purchase message comes in or when an event message comes
export const updateTimeslotsCollection = async (
    event?: Event,
    item?: ExperiencePurchaseItem,
    itemInCache?: PurchaseItem,
) => {
    const { date: dashboardDate, getMode, filters } = useDashboardFilterStore.getState();

    // If the Product filtered in Dashboard, check incoming item product id applied in that list
    const selectedProducts = filters.products.all === false ? filters.products.selected : [];
    if (selectedProducts.length > 0 && item?.product?.id && !selectedProducts.includes(item.product.id)) {
        return;
    }

    const eventId = event?.id ?? item?.event?.id;
    const start = event ? getStart(event) : item ? item.arrivalDateTime : null;
    const dashboardMode = getMode();
    const isListMode = dashboardMode === DashboardMode.LIST;
    const isTableMode = dashboardMode === DashboardMode.TABLE;
    if (!eventId || !start) {
        return;
    }

    const timeslotsQueryData = getTimeslotsQueryData(start);
    if (!timeslotsQueryData) {
        return;
    }

    const queryKey: any = timeslotsQueryData[0];
    const queryValue: any = timeslotsQueryData[1];

    // Query key is not available, the event should not be shown in the current view, no need to update timeslots collection
    if (isListMode && !queryKey) {
        return;
    }

    const range = getRangeByMode(dashboardMode);
    const startDate = now(dashboardDate).startOf("day");
    const endDate = range ? start.add(range, "days") : null;

    const timeslotsData = queryValue?.pages; // Seeing some errors in sentry where pages doesnt exist
    const isEmptyData = isEmpty(timeslotsData?.[0].data);
    const isTimeslotVisible =
        isListMode || isTableMode ? true : start.isSameOrAfter(startDate) && start.isSameOrBefore(endDate);

    // if no event or timeslot is not visible in UI, no need to update collection
    if (!isTimeslotVisible || queryKey.length !== 4) {
        return;
    }

    // If there is not data for a queryKey, then invalidate the query, no need to show placeholders
    if (isEmptyData) {
        queryClient.invalidateQueries(queryKey);
        return;
    }

    let timeslotId = item ? getTimeslotIdFromPurchaseItem(item) : null;
    if (event && !timeslotId) {
        timeslotId = getTimeslotIdByEvent(event);
    }

    if (!timeslotId) {
        return;
    }

    const validTimeslotId = timeslotId;
    const experienceId = event?.experience.id ?? item?.product.id;
    const experienceName = queryClient.getQueryData<Experience>(["products", experienceId])?.name ?? "";
    const isDayMode = dashboardMode === DashboardMode.DAY;
    const isReserved = filters.scope === DashboardScope.RESERVED;
    const isRelevantMode = isListMode || isDayMode || isTableMode;
    const hasReservedQuantity = (event?.quantity.reserved ?? 0) > 0;
    const shouldUpdateReservedView = event ? hasReservedQuantity : isRelevantMode && isReserved;
    const existingTimeslot = getResourceByQueryKey(queryKey, timeslotId);

    try {
        if (shouldUpdateReservedView) {
            if (existingTimeslot || itemInCache) {
                useDashboardFilterStore.getState().setIsRealTimeUpdate(true);
                fetchTimeslotAndUpdateCache(queryKey, validTimeslotId, eventId, itemInCache);
            } else {
                const isTimeslotExists = await updateTimeslotInCache(queryKey, validTimeslotId, eventId);
                if (itemInCache && "arrivalDateTime" in itemInCache) {
                    handleChangeArrival(queryKey, itemInCache, validTimeslotId);
                }

                if (isTimeslotExists) {
                    return;
                }

                // Fetch and insert to cache in case of new timeslots in dashboard table view
                if (isTableMode) {
                    fetchTimeslotAndUpdateCache(queryKey, validTimeslotId, eventId, itemInCache);
                    return;
                }

                updateCollectionByQueryKey<Timeslot>(queryKey, (timeslots: Timeslot[]) => {
                    const closestTimeslot = first(
                        sortBy(timeslots, (timeslot) => {
                            // Sort by distance in minutes.
                            return Math.abs(timeslot.arrivalDatetime?.diff(start, "minutes"));
                        }),
                    );

                    // One placeholder can have multiple events, we will sort them and add it in before/after array
                    return timeslots.map((timeslot) => {
                        if (timeslot === closestTimeslot) {
                            const placeholderData: TimeslotPlaceholderObject = {
                                start,
                                name: experienceName,
                                id: eventId,
                            };
                            const { placeholder } = timeslot;
                            return {
                                ...timeslot,
                                placeholder: start.isBefore(timeslot.arrivalDatetime)
                                    ? {
                                          queryKey: queryKey,
                                          after: timeslot.placeholder?.after,
                                          before: addUniqueTimeslotToPlaceholder(placeholderData, placeholder?.before),
                                      }
                                    : {
                                          queryKey: queryKey,
                                          before: timeslot.placeholder?.before,
                                          after: addUniqueTimeslotToPlaceholder(placeholderData, placeholder?.after),
                                      },
                            };
                        }

                        return timeslot;
                    });
                });
            }
        } else {
            fetchTimeslotAndUpdateCache(queryKey, validTimeslotId, eventId, itemInCache);
        }
    } catch (error) {
        Sentry.captureException(error, {
            tags: { module: "updateTimeslotsCollection" },
            extra: {
                debugInfo: "Error happened in updateTimeslotsCollection",
                timeslotId: validTimeslotId,
            },
        });

        console.error(`Error updating timeslots collection cache for timeslot: ${validTimeslotId}`, error);
    }
};
export const getRangeByMode = (mode: DashboardMode) => {
    switch (mode) {
        case DashboardMode.LIST:
            return 2;
        case DashboardMode.DAY:
            return 1;
        case DashboardMode.EQUIPMENT:
            return 1;
        case DashboardMode.WEEK:
            return 7;
        case DashboardMode.MONTH:
            return 31;
        default:
            return 0;
    }
};
const handleChangeArrival = async (queryKey: QueryKey, itemInCache: ExperiencePurchaseItem, timeslotId: ID) => {
    const previousTimeslotId = getTimeslotIdFromPurchaseItem(itemInCache);

    if (timeslotId === previousTimeslotId) {
        return;
    }

    // This is the case for arrival change for a purchase item, checking if we want to replace existing event id in dashboard or not
    const timeslotsQueryData = getTimeslotsQueryData(itemInCache.arrivalDateTime);
    if (!timeslotsQueryData) {
        return;
    }

    const previousEventId = itemInCache.event?.id;
    const previousEventQueryKey: QueryKey = timeslotsQueryData[0];

    if (!previousEventId) {
        removeCacheItem("timeslots", previousTimeslotId, previousEventQueryKey);
        return;
    }

    // Fetch the previous timeslot and remove if not found
    const previousTimeslot = await fetchTimeslotByEvent(previousEventId);
    const isTimeslotExists = await updateTimeslotInCache(queryKey, previousTimeslotId, previousEventId);
    if (isTimeslotExists) {
        return;
    }

    if (!previousTimeslot) {
        removeCacheItem("timeslots", previousTimeslotId, previousEventQueryKey);
    }
};

export const getAttendeesUniqueness = (experiences: Experience[]) => {
    const allAttendeesUnique = experiences.every((exp) => hasUniqueGuestIdentification(exp));
    const someAttendeesUnique = allAttendeesUnique
        ? true
        : experiences.some((exp) => hasUniqueGuestIdentification(exp));

    return { allAttendeesUnique, someAttendeesUnique };
};

export const updatePurchaseAndTimeslotsCache = async (purchaseFromPusher: { id: ID }) => {
    try {
        const purchaseId = purchaseFromPusher.id;

        const { data: purchase } = await api.get<Purchase>(`/purchases/${purchaseId}`, {
            params: { include: "form", expand: "waiver" },
        });

        if (!purchase.items?.length) {
            // Dummy purchase created on source seller when a partner experience is booked.
            // It has no items — no dashboard/timeslot/purchaseItem or purchases list updates needed.
            return;
        }

        const purchaseFromCache = queryClient.getQueryData<Purchase>(["purchases", purchaseId]);

        // Update the resource for form. waiver queryKey which will be used in purchase details page
        updateResourceCache("purchases", purchase, ["purchases", purchaseId, { include: "form", expand: "waiver" }]);

        const { filters, from, to, setIsRealTimeRefetch } = usePurchaseFilterStore.getState();
        const { searchQuery } = usePurchaseSearchStore.getState();

        const currentUser = queryClient.getQueryData<AccountState>(["account"])?.user;

        // If advanced filter is not applied, or searchQuery not applied we will update the purchases collection,
        // else we will invalidate the purchases filters query only when its a new purchase
        if (isEqual(filters, defaultFilters) && !from && !to && !searchQuery) {
            const replaceId = purchaseFromCache ? purchase.id : undefined;
            const isNewPurchase = !replaceId;

            // For affiliate users, skip direct cache insertion of new purchases —
            // the blind prefix-based cache write bypasses the partner filter.
            // Let invalidateQueries re-fetch with the correct partner filter instead.
            if (isNewPurchase && currentUser && isAffiliate(currentUser)) {
                setIsRealTimeRefetch(true);
                queryClient.invalidateQueries(["purchases", "collection"], { refetchActive: true });
            } else {
                updateCache("purchases", purchase, false, replaceId, !replaceId);
            }
        } else if (!purchaseFromCache) {
            setIsRealTimeRefetch(true);
            queryClient.invalidateQueries(["purchases", "collection"], {
                refetchActive: true,
            });
        }

        if (purchase.notifyAt) {
            updatePurchaseNotificationsCache(purchase);
        }

        queryClient.invalidateQueries(["audits", "collection", { objectId: purchaseId, objectClass: "purchase" }]);

        invalidateSellerBalance();

        // Check if we should skip updating timeslots cache
        const isDashboard = window.location.pathname.startsWith("/dashboard");
        const shouldUpdateTimeslots = window.location.pathname === "/" || isDashboard;
        const isTableMode = isTableView();

        for (const purchaseItem of purchase.items) {
            useDashboardFilterStore.getState().setIsRealTimeUpdate(true);

            let itemInCache = purchaseFromCache?.items.find(({ id }) => id === purchaseItem.id);
            if (!itemInCache) {
                itemInCache = queryClient.getQueryData<PurchaseItem>(["purchaseItems", purchaseItem.id]);
            }

            // Update timeslots only when the user is on dashboard page
            if (shouldUpdateTimeslots && "arrivalDateTime" in purchaseItem) {
                updateTimeslotsCollection(undefined, purchaseItem, itemInCache);
            }

            // Always update the purchaseItem in cache
            updateResourceCache("purchaseItems", purchaseItem);

            if (purchaseItem.object === PurchaseItemObject.WARE) {
                refetchMerchandiseCatalogItems(purchaseItem.product.id);
            }

            if (!purchaseItem.event?.id) {
                continue;
            }

            const queryKey =
                isTableMode && "arrivalDateTime" in purchaseItem
                    ? getQueryKeyForDashboardTable(purchaseItem.arrivalDateTime)
                    : getQueryKeyForRosterGuestList(purchaseItem.event.id);

            const itemIds = purchase.items.map((item: PurchaseItem) => ({ id: item.id }));
            const updatedPurchaseItem = {
                ...purchaseItem,
                purchase: {
                    ...purchase,
                    items: itemIds as PurchaseItem[],
                },
            };

            updateCollectionByQueryKey<PurchaseItem>(queryKey, (purchaseItems: PurchaseItem[]) => {
                const existingItemIndex = purchaseItems.findIndex(({ id }) => id === purchaseItem.id);
                if (existingItemIndex !== -1) {
                    return purchaseItems.map((itemItr) =>
                        itemItr.id === purchaseItem.id ? updatedPurchaseItem : itemItr,
                    );
                }

                return [...purchaseItems, updatedPurchaseItem];
            });

            // Remove the item from cache if it belongs to a different event
            if (itemInCache?.event?.id && itemInCache.event.id !== purchaseItem.event?.id) {
                const purchaseItemQueryKey = getQueryKeyForRosterGuestList(itemInCache.event.id);
                removeCacheItem("purchaseItems", itemInCache.id, purchaseItemQueryKey);
            }
        }
    } catch (error) {
        Sentry.captureException(error, {
            tags: { module: "updatePurchaseAndTimeslotsCache" },
            extra: {
                debugInfo: "Error happened in updatePurchaseAndTimeslotsCache",
                purchase: purchaseFromPusher.id,
            },
        });
        console.error(`Error updating purchases and purchaseItems, purchase: ${purchaseFromPusher.id}`, error);
    }
};
